import { useEffect, useState, useRef, useCallback } from 'react';
import { WebSocketManager } from './WebSocketManager';
import { CommonWebSocketReturn, MessageHandler, WebSocketStatus, WSEventCategory } from './types';
import { MessageHeader, RequestID } from '@/api/spec';
import useAppStore from '../../store/rootStore.ts';

interface UseWebSocketConfig {
  endpoint: string;
  canConnect: boolean;
  onMessage: MessageHandler;
  onError?: (category: WSEventCategory, code: number, reason: string) => void;
}

export function useWebSocket({ endpoint, canConnect, onMessage, onError }: UseWebSocketConfig): CommonWebSocketReturn {
  const [status, setStatus] = useState<WebSocketStatus>('CLOSED');
  const [retryCount, setRetryCount] = useState(0);
  const subscriptionRef = useRef<ReturnType<WebSocketManager['subscribe']> | null>(null);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const manager = WebSocketManager.getInstance();

    const subscription = manager.subscribe(
      endpoint,
      (message) => onMessageRef.current(message),
      (newStatus, newRetryCount) => {
        setStatus((prev) => (prev !== newStatus ? newStatus : prev));
        setRetryCount((prev) => (prev !== newRetryCount ? newRetryCount : prev));
      },
      (category, code, reason) => {
        if (category === 'unauthorized') {
          useAppStore.getState().resetToWalletConnected();
        }
        if (onErrorRef.current) {
          onErrorRef.current?.(category, code, reason);
        }
      }
    );

    subscriptionRef.current = subscription;

    setStatus(subscription.getStatus());
    setRetryCount(subscription.getRetryCount());

    if (canConnect) {
      subscription.connect();
    }

    return () => {
      subscription.disconnect();
      subscriptionRef.current = null;
    };
  }, [endpoint, canConnect]);

  const sendMessage = useCallback(<T extends MessageHeader>(message: T): RequestID | undefined => {
    return subscriptionRef.current?.send(message);
  }, []);

  return { status, sendMessage, retryCount };
}
